import { useEffect, useState } from 'react';

import { Button, Input, Select, Form, Alert } from 'antd';
import moment from 'moment';

import { selectDefaultProvider, sortByIPA } from 'utils/helpers';
import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { ProviderType } from 'utils/enums';

import FloatLabel from 'components/Common/FloatLabel';
import DatePickerLocalised from 'components/Common/DatePickerLocalised';

import './index.scss';

const { Option } = Select;

interface SearchClaimsFormProps {
  getClaimRead(userId: string): any,
  isFetchingClaimRead: boolean,
  search(searchObject: Object): any,
  isSearching: boolean,
  setDataSource(data: any[]): void,
};

const SearchClaimsForm = (props: SearchClaimsFormProps) => {

  const ALL_PROVIDERS = '{"Provider": "ALL PROVIDERS","RmiProvKeyId": null,"RmiCompanyId": null}';

  const [form] = Form.useForm();
  const [provider, setProvider] = useState(ALL_PROVIDERS);
  const [providerList, setProviderList] = useState<any[]>([]);
  const [showHelpfulText, setShowHelpfulText] = useState(false);
  const [birth, setBirth] = useState('');

  useEffect(() => {
    getClaimReadProviderList();
  }, []);

  useEffect(() => {
    birth !== '' && form.validateFields();
  }, [birth]);

  const getClaimReadProviderList = () => {
    return props.getClaimRead(sessionStorage.getItem('user_id')!)
      .then((response: any[]) => {

        form.setFieldsValue({
          provider: selectDefaultProvider(response, ALL_PROVIDERS, 'string')
        });

        setProviderList(sortByIPA(response, 'Provider'));
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const validationRules = async (fieldData: any, value: any) => {

    const charLessThanFive = (value: any) => fieldData.field === 'memberId' && value && value.trim().length < 5;

    const allFieldsEmpty = () => {
      return !form.getFieldValue('memberId') && !birth &&
        !form.getFieldValue('firstname') && !form.getFieldValue('lastname') &&
        !form.getFieldValue('claimRef') &&
        !form.getFieldValue('claimStatus');
    }

    const allFieldsResolved = () => {
      return (form.getFieldValue('memberId') && birth) ||
        (form.getFieldValue('firstname') && form.getFieldValue('lastname') && birth) ||
        form.getFieldValue('claimRef') ||
        form.getFieldValue('claimStatus');
    }

    if (charLessThanFive(value)) {
      return Promise.reject('Please enter at least 5 characters.');
    }

    if (allFieldsEmpty()) {
      setShowHelpfulText(true);
      return Promise.reject('Required');
    }

    if (allFieldsResolved()) {
      setShowHelpfulText(false);
      return Promise.resolve();
    }

    if (JSON.parse(provider || '{}').Provider === 'ALL PROVIDERS' || ![ProviderType.PCP, ProviderType.CLINIC].includes(JSON.parse(provider || '{}').RmiClass)) {

      if (fieldData.field === 'memberId') {
        const memberIdResolved = () => {
          return form.getFieldValue('memberId') || (form.getFieldValue('claimStatus') || form.getFieldValue('claimRef')) || (form.getFieldValue('firstname') && form.getFieldValue('lastname'));
        }

        if (memberIdResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (birth) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'birth') {
        const dobResolved = () => {
          return birth || (form.getFieldValue('claimStatus') || form.getFieldValue('claimRef'));
        }

        const atleastOneField = () => {
          return form.getFieldValue('memberId') || (form.getFieldValue('firstname') || form.getFieldValue('lastname'));
        }

        if (dobResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (atleastOneField()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'claimRef') {
        const claimRefResolved = () => {
          return form.getFieldValue('claimRef') || birth;
        }

        if (claimRefResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'claimStatus') {
        const claimStatusResolved = () => {
          return form.getFieldValue('claimStatus') || birth;
        }

        if (claimStatusResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'firstname') {
        const firstNameResolved = () => {
          return (form.getFieldValue('firstname') || (form.getFieldValue('claimStatus') || form.getFieldValue('claimRef')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('lastname'));
        }

        const missingDob = () => {
          return form.getFieldValue('lastname') && !birth;
        }

        if (firstNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDob()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'lastname') {
        const lastNameResolved = () => {
          return (form.getFieldValue('lastname') || (form.getFieldValue('claimStatus') || form.getFieldValue('claimRef')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('firstname'));
        }

        const missingDob = () => {
          return form.getFieldValue('firstname') && !birth;
        }

        if (lastNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDob()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }
    }

    if ([ProviderType.PCP, ProviderType.CLINIC].includes(JSON.parse(provider || '{}').RmiClass)) {

      if (fieldData.field === 'memberId') {
        const memberIdResolved = () => {
          return form.getFieldValue('memberId') || (form.getFieldValue('claimStatus') || form.getFieldValue('claimRef'));
        }

        if (memberIdResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'birth') {
        const dobResolved = () => {
          return birth || (form.getFieldValue('claimStatus') || form.getFieldValue('claimRef')) || form.getFieldValue('memberId');
        }

        if (dobResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'claimRef') {
        const claimRefResolved = () => {
          return form.getFieldValue('claimRef') || birth;
        }

        if (claimRefResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'claimStatus') {
        const claimStatusResolved = () => {
          return form.getFieldValue('claimStatus') || birth;
        }

        if (claimStatusResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }
      }

      if (fieldData.field === 'firstname') {
        const firstNameResolved = () => {
          return (form.getFieldValue('firstname') || (form.getFieldValue('claimStatus') || form.getFieldValue('claimRef')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('lastname'));
        }

        const missingDob = () => {
          return form.getFieldValue('lastname') && !birth;
        }

        if (firstNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDob()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }

      if (fieldData.field === 'lastname') {
        const lastNameResolved = () => {
          return (form.getFieldValue('lastname') || (form.getFieldValue('claimStatus') || form.getFieldValue('claimRef')) || form.getFieldValue('memberId')) || (birth && !form.getFieldValue('firstname'));
        }

        const missingDob = () => {
          return form.getFieldValue('firstname') && !birth;
        }

        if (lastNameResolved()) {
          setShowHelpfulText(false);
          return Promise.resolve();
        }

        if (missingDob()) {
          setShowHelpfulText(false);
          return Promise.reject('Required');
        }
      }
    }
  };

  const onSearch = () => {
    form.validateFields().then(values => {
      const parsedProvider = JSON.parse(provider);
      setShowHelpfulText(false);
      let formData = {
        ClaimNo: values.claimRef,
        CompanyId: parsedProvider.RmiCompanyId,
        DateOfBirth: birth !== '' ? birth : null,
        MemberId: values.memberId,
        Provider: provider,
        ProviderName: parsedProvider.Provider,
        ProvKeyId: parsedProvider.RmiProvKeyId,
        FirstName: values.firstname,
        LastName: values.lastname,
        Status: values.claimStatus ? JSON.parse(values.claimStatus) : null,
        StatusValue: values.claimStatus ? JSON.parse(values.claimStatus).Code : null,
        UserId: sessionStorage.getItem('user_id'),
        index: 0,
      };
      localStorage.setItem("print_ipa", parsedProvider.RmiCompanyId);
      return props.search(formData)
        .then((response: any) => {
          if (response.length === 0) notifier.error('There were no results for this search. Please check the fields and try again.');
          props.setDataSource(response);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    }).catch(err => {
      setShowHelpfulText(true);
    })
  };

  const onReset = () => {
    props.setDataSource([]);
    form.resetFields();
    form.setFieldsValue({
      provider: selectDefaultProvider(providerList, ALL_PROVIDERS, 'string')
    });
    setProvider(ALL_PROVIDERS);
    setBirth('');
    setShowHelpfulText(false);
  };

  return (
    <Form
      form={form}
      name="claimSearchForm"
      onFinish={onSearch}
      initialValues={{
        provider: JSON.stringify(ALL_PROVIDERS)
      }}
    >
      <div className="member-search row flex-lg-wrap justify-content-start mb-3 ml-0">
        <div className="col-lg-6">
          <FloatLabel
            label="Select a Provider..."
            name="provider"
            form={form}
          >
            <Select
              showSearch
              value={provider}
              onChange={value => setProvider(value)}
              loading={props.isFetchingClaimRead}
            >
              <Option value={ALL_PROVIDERS}>ALL PROVIDERS</Option>
              {providerList.map((option: any, key: number) => {
                return <Option key={key} value={JSON.stringify(option)}>{option.Provider}</Option>;
              })}
            </Select>
          </FloatLabel>
        </div>

        <div className="col-lg-6">
          <FloatLabel
            label="Claim#/Claim Ref:"
            name="claimRef"
            form={form}
            dependencies={['memberId', 'firstname', 'lastname', 'claimRef', 'claimStatus']}
            rules={[
              { validator: validationRules }
            ]}
          >
            <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6">
          <FloatLabel
            label="Status:"
            name="claimStatus"
            form={form}
            dependencies={['memberId', 'firstname', 'lastname', 'claimRef', 'claimStatus']}
            rules={[
              { validator: validationRules }
            ]}
          >
            <Select>
              <Option value={JSON.stringify({ Code: '1', Descr: 'Claim Under Review' })} label="CLAIM UNDER REVIEW">CLAIM UNDER REVIEW</Option>
              <Option value={JSON.stringify({ Code: '2', Descr: 'Finalized' })} label="FINALIZED">FINALIZED</Option>
              <Option value={JSON.stringify({ Code: '3', Descr: 'Pending Accounting Check Release' })} label="PENDING ACCOUNTING CHECK RELEASE">PENDING ACCOUNTING CHECK RELEASE</Option>
              <Option value={JSON.stringify({ Code: '4', Descr: 'Pending Eligibility Check' })} label="PENDING ELIGIBILITY CHECK">PENDING ELIGIBILITY CHECK</Option>
              <Option value={JSON.stringify({ Code: '5', Descr: 'Pending UM Review' })} label="PENDING UM REVIEW">PENDING UM REVIEW</Option>
              <Option value={JSON.stringify({ Code: '6', Descr: 'Provider Record Not Found or Has Discrepancy - Please Refer to Request Letter Sent' })} label="PROVIDER RECORD NOT FOUND OR HAS DISCREPANCY - PLEASE REFER TO REQUEST LETTER SENT">PROVIDER RECORD NOT FOUND OR HAS DISCREPANCY - PLEASE REFER TO REQUEST LETTER SENT</Option>
              <Option value={JSON.stringify({ Code: '7', Descr: 'Unclean Claim - Please Refer to Letter Mailed Out' })} label="UNCLEAN CLAIM - PLEASE REFER TO LETTER MAILED OUT">UNCLEAN CLAIM - PLEASE REFER TO LETTER MAILED OUT</Option>
            </Select>
          </FloatLabel>
        </div>

        <div className="col-lg-6">
          <FloatLabel
            label="Member ID:"
            name="memberId"
            form={form}
            dependencies={['memberId', 'firstname', 'lastname', 'claimRef', 'claimStatus']}
            rules={[
              { validator: validationRules }
            ]}
          >
            <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6">
          <FloatLabel
            label="Last Name"
            name="lastname"
            form={form}
            dependencies={['memberId', 'firstname', 'lastname', 'claimRef', 'claimStatus']}
            rules={[
              { validator: validationRules }
            ]}
          >
            <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6">
          <FloatLabel
            label="First Name"
            name="firstname"
            form={form}
            dependencies={['memberId', 'firstname', 'lastname', 'claimRef', 'claimStatus']}
            rules={[
              { validator: validationRules }
            ]}
          >
            <Input />
          </FloatLabel>
        </div>

        <div className="col-lg-6">
          <FloatLabel
            label="DOB (MM/DD/YYYY)"
            name="birth"
            value={birth}
            form={form}
            dependencies={['memberId', 'firstname', 'lastname', 'claimRef', 'claimStatus']}
            rules={[
              { validator: validationRules },
              () => ({
                async validator() {
                  const validDate = () => birth && moment(birth).isValid() && moment(birth).year() >= 1900 && birth.match(/^\d{2}\/\d{2}\/\d{4}$/);
                  if (validDate()) {
                    return Promise.resolve();
                  }

                  if (!showHelpfulText && birth) {
                    return Promise.reject(new Error('Invalid Date'));
                  }
                }
              })
            ]}
          >
          <DatePickerLocalised
            date={birth}
            setDate={setBirth}
          />
          </FloatLabel>
        </div>

        <div className="col-lg-6 mt-2">
          <Form.Item>
            <div className="d-flex justify-content-end">
              <Button type="primary" style={{ width: "50%" }} htmlType={'submit'} loading={props.isSearching}>SEARCH</Button>
              <Button type="default" style={{ width: "50%" }} onClick={onReset}>RESET</Button>
            </div>
          </Form.Item>
        </div>

        {showHelpfulText && (
          <Alert
            message={[ProviderType.PCP, ProviderType.CLINIC].includes(JSON.parse(provider || '{}').RmiClass) ?
              <>
                Please meet one of the following requirements:<br />
                MemberID ONLY <br />
                Claim# / Claim Ref ONLY <br />
                Status ONLY <br />
                Last Name + First Name ONLY <br />
                DOB (mm/dd/yyyy) ONLY
              </>
              :
              <>
                Please meet one of the following requirements:<br />
                MemberID + DOB (mm/dd/yyyy) <br />
                Claim# / Claim Ref ONLY <br />
                Status ONLY <br />
                Last Name + First Name + DOB (mm/dd/yyyy)
              </>
            }
            type="error"
            className="col-lg-24"
          />
        )}
      </div>
    </Form>
  );
}

export default SearchClaimsForm;